import React from "react";
import { Tag } from "antd";
import {
  FilePdfOutlined,
  FileImageOutlined,
  FileExcelOutlined,
  FileTextOutlined,
  FileOutlined,
} from "@ant-design/icons";
import { TicketStatus, TicketPriority } from "../../../Services/TicketServices";

/**
 * Native utility to fetch file from a URL and trigger browser download
 */
export const downloadFile = async (fileUrl: string, fileName: string): Promise<boolean> => {
  if (!fileUrl) return false;
  let targetUrl = fileUrl;
  if (!fileUrl.startsWith("http://") && !fileUrl.startsWith("https://") && !fileUrl.startsWith("blob:") && !fileUrl.startsWith("data:")) {
    const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;
    targetUrl = `${baseUrl.replace(/\/$/, "")}/${fileUrl.replace(/^\//, "")}`;
  }

  try {
    const response = await fetch(targetUrl);
    if (!response.ok) throw new Error(`File not found (HTTP ${response.status})`);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || "attachment";
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    return true;
  } catch (err: any) {
    if (targetUrl.startsWith("data:")) {
      try {
        const link = document.createElement("a");
        link.href = targetUrl;
        link.download = fileName || "attachment";
        document.body.appendChild(link);
        link.click();
        link.remove();
        return true;
      } catch { /* fallback */ }
    }
    throw err;
  }
};

/**
 * Returns a specific document icon depending on file name extension
 */
export const getFileIcon = (fileName: string = "") => {
  const ext = fileName.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "pdf":
      return <FilePdfOutlined style={{ color: "#fa541c", fontSize: 16 }} />;
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
    case "webp":
      return <FileImageOutlined style={{ color: "#52c41a", fontSize: 16 }} />;
    case "xlsx":
    case "xls":
    case "csv":
      return <FileExcelOutlined style={{ color: "#13c2c2", fontSize: 16 }} />;
    case "txt":
    case "doc":
    case "docx":
      return <FileTextOutlined style={{ color: "#1890ff", fontSize: 16 }} />;
    default:
      return <FileOutlined style={{ color: "#8c8c8c", fontSize: 16 }} />;
  }
};

/**
 * Returns custom Tag component for ticket statuses
 */
export const getStatusTag = (status: TicketStatus) => {
  const map: Record<number, { color: string; text: string }> = {
    [TicketStatus.Open]: { color: "processing", text: "Open" },
    [TicketStatus.Closed]: { color: "default", text: "Closed" },
    [TicketStatus.InProgress]: { color: "purple", text: "In progress" },
    [TicketStatus.UnderResearch]: { color: "cyan", text: "Under research" },
    [TicketStatus.InTesting]: { color: "warning", text: "In testing" },
    [TicketStatus.ReadyForRelease]: { color: "success", text: "Ready for release" },
  };
  const s = map[status] || { color: "default", text: "Unknown" };
  return (
    <Tag
      color={s.color}
      style={{
        margin: 0,
        borderRadius: 12,
        fontWeight: 600,
        border: "none",
        padding: "2px 10px",
      }}
    >
      {s.text}
    </Tag>
  );
};

/**
 * Returns custom Tag component for ticket priorities
 */
export const getPriorityTag = (priority: TicketPriority) => {
  const map: Record<number, { color: string; text: string }> = {
    [TicketPriority.P4]: { color: "success", text: "Low" },
    [TicketPriority.P3]: { color: "blue", text: "Medium" },
    [TicketPriority.P2]: { color: "warning", text: "High" },
    [TicketPriority.P1]: { color: "error", text: "Urgent" },
  };
  const p = map[priority] || { color: "default", text: "N/A" };
  return (
    <Tag
      color={p.color}
      style={{
        margin: 0,
        borderRadius: 12,
        fontWeight: 600,
        border: "none",
        padding: "2px 10px",
      }}
    >
      {p.text}
    </Tag>
  );
};
